import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';

import PaymentVippsGeneric from '../../components/payment/vippsGeneric';
import PaymentBank from '../../components/payment/bank';
import PaymentPaypalGeneric from '../../components/payment/paypalGeneric';
import PaymentReceipt from '../../components/payment/receipt';

const getReservationSeconds = (contestant = {}, now = Date.now()) => {
    if (!contestant.paymentReservationExpiresAt) {
        return 0;
    }
    const expiresAt = new Date(contestant.paymentReservationExpiresAt).getTime();
    if (Number.isNaN(expiresAt)) {
        return 0;
    }
    const serverNow = new Date(contestant.paymentReservationServerNow).getTime();
    const clientLoadedAt = Number(contestant.paymentReservationClientLoadedAt);
    if (!Number.isNaN(serverNow) && clientLoadedAt > 0) {
        const remainingAtLoad = expiresAt - serverNow;
        return Math.max(0, Math.ceil((remainingAtLoad - (now - clientLoadedAt)) / 1000));
    }
    return Math.max(0, Math.ceil((expiresAt - now) / 1000));
};

const formatCountdown = seconds => {
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0');
    const remainingSeconds = (seconds % 60).toString().padStart(2, '0');
    return `${minutes}:${remainingSeconds}`;
};

@observer
class RaceClassContestantPayGeneric extends Component {
    constructor(props) {
        super(props);
        this.state = {
            paymentInProgress: false,
            reservationNow: Date.now(),
        };
        this.reservationExpiryHandled = false;
        this.reservationExpiryRetryAt = 0;
        this.reservationExpiryRetryCount = 0;
    }

    getPaymentMethods = () => {
        const { raceStore, raceClassContestantStore } = this.props.stores;
        const { race, customers } = raceStore;
        const { raceClassContestant } = raceClassContestantStore;
        const currentCustomer = customers?.find(c => c.id === race.customer);

        const paymentMethods = [];
        if (race.forcePaymentBeforeSignupApproval) {
            paymentMethods.push(...(race.signupOnlinePaymentMethods || []).filter(method => (
                method !== 'vipps' || (raceClassContestant.requiredPaymentCurrency || 'NOK') === 'NOK'
            )));
        } else {
            if (currentCustomer?.featureFlags?.hasVippsPayment) {
                paymentMethods.push('vipps');
            }
            if (currentCustomer?.featureFlags?.hasBankPayment) {
                paymentMethods.push('bank');
            }
            if (currentCustomer?.featureFlags?.hasPaypalPayment) {
                paymentMethods.push('paypal');
            }
        }
        return paymentMethods;
    }

    choosePaymentMethod = async (e) => {
        const { method } = e.target.closest('button').dataset;
        const { raceClassContestantStore } = this.props.stores;
        const { raceClassContestant } = raceClassContestantStore;
        await raceClassContestantStore.saveField(raceClassContestant.id, 'paymentMethod', method, true);
    }

    paymentCallback = async () => {
        // console.log('paymentCallback', data);
        this.setState({ paymentInProgress: true });
    }

    paymentDoneCallback = async ({ paymentStatus }) => {
        const { raceClassContestantStore, userStore } = this.props.stores;
        const { raceClassContestant } = raceClassContestantStore;
        if (paymentStatus === 2 || paymentStatus === 3) {
            await raceClassContestantStore.loadContestants({
                race: raceClassContestant.race,
                raceClass: raceClassContestant.raceClass,
                user: userStore.user.id,
                emptyFirst: false,
            });
        }
        // console.log('paymentDoneCallback', data);
        this.setState({ paymentInProgress: false });
    }

    payment = () => {
        const {
            paymentType,
            amount,
            paymentTitle,
        } = this.props;
        const paymentMethods = this.getPaymentMethods();
        const { raceClassContestantStore, raceStore } = this.props.stores;
        const { race } = raceStore;
        const { raceClassContestant } = raceClassContestantStore;
        const { customer } = race;
        // The payment components read the contestant from the store at click time. After a
        // reservation expires the store is reloaded and comes back empty, and a payment created
        // from an empty contestant has no signup behind it - the backend now refuses it, but
        // there is nothing sensible to show the buyer either, so stop here.
        if (!(raceClassContestant?.id > 0)) {
            return <div class='alert alert-danger mb-0' role='alert'>
                <Text id='race-class.signup.error-no-signup'>
                    Your signup could not be found. Please sign up again before paying.
                </Text>
            </div>;
        }
        let paymentMethod = raceClassContestant.paymentMethod || paymentMethods[0];
        if (!paymentMethods.includes(paymentMethod)) {
            paymentMethod = paymentMethods[0];
        }
        switch (paymentMethod) {
            case 'vipps':
                return <PaymentVippsGeneric
                    stores={this.props.stores}
                    title={paymentTitle}
                    type={paymentType}
                    amount={amount}
                    customer={customer}
                    race={raceClassContestant.race}
                    raceClassContestant={raceClassContestant.id}
                    callback={this.paymentCallback}
                    callbackPaymentDone={this.paymentDoneCallback}
                />;
            case 'bank':
                return <PaymentBank
                    stores={this.props.stores}
                    title={paymentTitle}
                    type={paymentType}
                    amount={amount}
                    customer={customer}
                    race={raceClassContestant.race}
                    raceClassContestant={raceClassContestant.id}
                />;
            case 'paypal':
                return <PaymentPaypalGeneric
                    stores={this.props.stores}
                    title={paymentTitle}
                    type={paymentType}
                    amount={amount}
                    currency={raceClassContestant.requiredPaymentCurrency || 'NOK'}
                    customer={customer}
                    race={raceClassContestant.race}
                    raceClassContestant={raceClassContestant.id}
                    callback={this.paymentCallback}
                    callbackPaymentDone={this.paymentDoneCallback}
                />;
            default:
                return <div class='alert alert-danger mb-0' role='alert'>
                    <Text id='race-class.signup.error-payment-config'>
                        Online payment is not available for this race class. Contact the organizer.
                    </Text>
                </div>;
        }
    }

    receipt = () => {
        const {
            paymentType,
            amount,
            paymentTitle,
        } = this.props;
        const { raceClassContestantStore } = this.props.stores;
        const { raceClassContestant } = raceClassContestantStore;

        return <PaymentReceipt
            stores={this.props.stores}
            title={paymentTitle}
            type={paymentType}
            amount={amount}
            race={raceClassContestant.race}
            raceClassContestant={raceClassContestant.id}
        />;
    }

    componentDidMount() {
        this.reservationTimer = setInterval(this.updateReservationClock, 1000);
    }

    componentWillUnmount() {
        clearInterval(this.reservationTimer);
    }

    updateReservationClock = async () => {
        const { raceClassContestantStore, userStore, appState } = this.props.stores;
        const { raceClassContestant } = raceClassContestantStore;
        const field = `paid${util.ucfirst(this.props.paymentType)}`;
        const reservationPending = raceClassContestant.paymentRequiredBeforeApproval
            && !raceClassContestant.approved
            && !raceClassContestant[field]
            && raceClassContestant.requiredPaymentType === this.props.paymentType;
        if (!reservationPending) {
            return;
        }
        const reservationNow = Date.now();
        this.setState({ reservationNow });
        if (getReservationSeconds(raceClassContestant, reservationNow) > 0
            || this.reservationExpiryHandled
            || reservationNow < this.reservationExpiryRetryAt) {
            return;
        }
        this.reservationExpiryHandled = true;
        const loadResult = await raceClassContestantStore.loadContestants({
            race: raceClassContestant.race,
            raceClass: raceClassContestant.raceClass,
            user: userStore.user.id,
            emptyFirst: false,
            includeLoadStatus: true,
        });
        if (!loadResult.loaded || loadResult.contestant?.id) {
            this.reservationExpiryRetryCount += 1;
            const retryDelay = Math.min(30000, 5000 * (2 ** (this.reservationExpiryRetryCount - 1)));
            this.reservationExpiryRetryAt = Date.now() + retryDelay;
            this.reservationExpiryHandled = false;
            return;
        }
        const { callback = () => {}, drawerLevel = 1 } = this.props;
        callback();
        appState.closeDrawer(false, drawerLevel);
    }

    render() {
        const {
            paymentInProgress,
            reservationNow,
        } = this.state;
        const paymentMethods = this.getPaymentMethods();
        const {
            paymentTitle,
            paymentType,
        } = this.props;
        const { userStore, raceClassContestantStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { raceClassContestant } = raceClassContestantStore;
        const field = `paid${util.ucfirst(paymentType)}`;
        const reservationPending = raceClassContestant.paymentRequiredBeforeApproval
            && !raceClassContestant.approved
            && !raceClassContestant[field]
            && raceClassContestant.requiredPaymentType === paymentType;
        const reservationSeconds = reservationPending
            ? getReservationSeconds(raceClassContestant, reservationNow)
            : 0;
        const reservationPaymentAvailable = !reservationPending || reservationSeconds > 0;

        return (<>
            <div class='w-100 d-flex justify-content-center'>
                <div class='w-100 h-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                    <div class='font-weight-lighter px-3 box-header mt-0'>
                        {paymentTitle}
                    </div>

                    {reservationPending && <div class='alert alert-danger mx-3 text-center' role='alert'>
                        <strong>
                            <Text id='race-class.signup.payment-countdown'>Time left to pay</Text>:{' '}
                            {formatCountdown(reservationSeconds)}
                        </strong>
                        <div class='mt-1'>
                            <Text id='race-class.signup.reservation-warning'>
                                Your signup is not approved yet. Complete the required online payment before the timer
                                reaches zero, or the signup will be deleted automatically and the spot will be released.
                            </Text>
                        </div>
                    </div>}

                    <div class='w-100 d-flex flex-column'>
                        <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                            {raceClassContestant[field] ? <>
                                {this.receipt()}
                            </> : reservationPaymentAvailable ? <>
                                {this.payment()}
                            </> : <>
                                <div class='alert alert-danger mb-0' role='alert'>
                                    <Text id='race-class.signup.payment-expiry-checking'>
                                        The payment time expired. Your signup is being refreshed.
                                    </Text>
                                </div>
                            </>}
                        </div>
                    </div>

                    {!paymentInProgress && !raceClassContestant[field] && reservationPaymentAvailable && <>
                        <div class='font-weight-lighter px-3 box-header mt-4'>
                            <Text id='race-class.payment-methods'>Payment methods</Text>
                        </div>
                        <div class='w-100 d-flex flex-column'>
                            <div class={`d-flex flex-column mx-3 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} px-4 py-3`}>
                                <div class='w-100 d-flex flex-row justify-content-center'>
                                    {paymentMethods.map(method => {
                                        return <button
                                            key={method}
                                            type='button'
                                            data-method={method}
                                            class={`btn btn-${method === raceClassContestant.paymentMethod ? 'primary' : 'outline-secondary'} rounded-pill me-2 mt-2`}
                                            onClick={this.choosePaymentMethod}
                                        >
                                            {method}
                                        </button>;
                                    })}
                                </div>

                            </div>
                        </div>
                    </>}

                </div>
            </div>
        </>);
    }
}

export default RaceClassContestantPayGeneric;
